import { useEffect, useState } from "react";
import { Mic, Play, Loader2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Audio playback panel for a training session.
 * Used by both the seller's Feedback page and the Admin Session Detail.
 *
 * - Calls GET /api/sessions/{sessionId}/recordings (presigned URLs, 1h)
 * - Renders a native <audio> player per voice turn with the transcript snippet
 * - Server-side audit-logs every access automatically (DSGVO)
 */
const SessionRecordingsPanel = ({ sessionId, eyebrow = "Anhören" }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/sessions/${sessionId}/recordings`);
        if (!mounted) return;
        setConfigured(!!data?.configured);
        setItems(data?.items || []);
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.detail || "Aufzeichnungen konnten nicht geladen werden");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="nv-card p-6" data-testid="recordings-panel-loading">
        <p className="label-eyebrow flex items-center gap-2"><Mic className="w-3.5 h-3.5 text-novatus-green" /> {eyebrow}</p>
        <div className="mt-4 text-sm text-novatus-text-3 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade Aufzeichnungen…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nv-card p-6 border-amber-500/40" data-testid="recordings-panel-error">
        <p className="label-eyebrow flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Aufzeichnungen</p>
        <p className="text-sm text-amber-300 mt-2">{error}</p>
      </div>
    );
  }

  // Storage not configured (e.g. Hetzner ENV vars missing in this environment).
  // Show an explicit info card so users / admins can see WHY no audio appears.
  if (!configured) {
    return (
      <div className="nv-card p-6 border-amber-500/40" data-testid="recordings-panel-not-configured">
        <p className="label-eyebrow flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Audio-Aufzeichnungen
        </p>
        <p className="text-sm text-amber-200 mt-2 leading-relaxed">
          Der Audio-Speicher ist in dieser Umgebung noch nicht konfiguriert. Sprach-Aufnahmen
          konnten daher nicht gespeichert werden.
        </p>
        <p className="text-[11px] text-novatus-text-3 mt-2 leading-relaxed">
          Admins: Bitte die <code className="text-novatus-green">HETZNER_S3_*</code>-Variablen im
          Secrets-Bereich prüfen und neu deployen. Bestehende Sessions können dadurch nicht
          rückwirkend aufgezeichnet werden.
        </p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="nv-card p-6" data-testid="recordings-panel-empty">
        <p className="label-eyebrow flex items-center gap-2"><Mic className="w-3.5 h-3.5 text-novatus-green" /> {eyebrow}</p>
        <p className="text-sm text-novatus-text-3 mt-3 leading-relaxed">
          Für diese Sitzung wurden keine Audio-Aufzeichnungen gespeichert. Mögliche Gründe:
        </p>
        <ul className="text-xs text-novatus-text-3 mt-2 space-y-1 list-disc list-inside leading-relaxed">
          <li>Reine Text-Übung (kein Voice-Modus aktiv)</li>
          <li>Aufnahme erfolgte vor Aktivierung des Audio-Speichers</li>
          <li>Browser-Mikrofon hat keine Audio-Daten geliefert</li>
        </ul>
      </div>
    );
  }

  return (
    <div className="nv-card p-6" data-testid="recordings-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="label-eyebrow flex items-center gap-2 text-novatus-green">
          <Mic className="w-3.5 h-3.5 text-novatus-green" /> {eyebrow}
        </p>
        <span className="text-[11px] uppercase tracking-wider font-bold text-novatus-text-2">
          {items.length} Sprach-Turn{items.length === 1 ? "" : "s"} · Löschung nach 90 Tagen
        </span>
      </div>

      <ul className="mt-4 space-y-3">
        {items.map((it, i) => (
          <li
            key={it.message_id || i}
            className="rounded-xl bg-novatus-card-2 border border-novatus p-3 sm:p-4"
            data-testid={`recording-item-${i}`}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40">
                <Play className="w-3 h-3" /> Verkäufer · Turn {i + 1}
              </span>
              {typeof it.duration_s === "number" && (
                <span className="text-[10px] text-novatus-text-3 font-mono">{it.duration_s.toFixed(1)}s</span>
              )}
            </div>
            {it.transcript && (
              <p className="text-xs text-novatus-text-2 italic mb-2 line-clamp-2">
                „{it.transcript}"
              </p>
            )}
            {/* Native browser audio player. The presigned URL is valid for 1 h. */}
            <audio
              controls
              preload="none"
              className="w-full"
              data-testid={`recording-audio-${i}`}
            >
              <source src={it.url} type={it.content_type || "audio/webm"} />
              Dein Browser unterstützt kein HTML5-Audio.
            </audio>
          </li>
        ))}
      </ul>

      <p className="text-[11px] text-novatus-text-2 mt-3 leading-relaxed">
        Hinweis: Jeder Zugriff wird zu Compliance-Zwecken im Audit-Log protokolliert. Aufzeichnungen
        werden serverseitig nach 90 Tagen automatisch gelöscht.
      </p>
    </div>
  );
};

export default SessionRecordingsPanel;
